/**
 * CurrencyTab 幣別價格 (S2.2) — the manually-locked per-currency price rows
 * (人工鎖定, Discribe-19/21) as a Price Setup surface.
 *
 * - READ view is open; the WRITE controls (add / edit) are visibility-gated
 *   on `canEditCostBasis()` (DA-7) — hidden for other roles, never disabled.
 * - ADD enables a currency for a product cell (product × level × currency ×
 *   price). Prices are human-typed — never FX-derived; a duplicate cell save
 *   upserts through the store reducer's cell key.
 * - EDIT scopes to PRICE ONLY: `CurrencyPriceRow` has no validity fields and
 *   none are added (unnamed schema change — refused this wave). Product,
 *   level and currency render as read-only text in the edit modal.
 * - UNLOCK-FIRST (Discribe-21): a `fixedPrice` row's price is immutable —
 *   `applyCurrencyPriceRowSave` throws FIXED_PRICE_LOCKED on a price change.
 *   The edit modal surfaces the lock (W6: read-only text + LockOutlined +
 *   tag — never a disabled input) and demands an explicit audited unlock
 *   save before the price field activates.
 * - The product-scoped empty state is LOAD-BEARING: it IS the explanation of
 *   the downstream refusal (`no_currency_price`) — dedicated copy, not the
 *   generic list triad. A missing row stays a refusal; nothing here
 *   fabricates a price.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  App,
  Button,
  Col,
  Dropdown,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import { LockOutlined, MoreOutlined, PlusOutlined, UnlockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import TableContainer, { ListEmptyState } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useCurrencies } from '@/hooks/useCurrencies';
import { useProductOptions } from '@/hooks/useProducts';
import { canEditCostBasis, usePricingDataset, usePricingMutations } from '@/hooks/usePricing';
import { PricingGuardError } from '@/stores/pricingStore';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { CurrencyPriceRow } from '@/types/api/pricing';
import { formatMoney } from '../salesOrders/components/soFormat';

const { Text } = Typography;

/** Select sentinel for the list-price (no-level) row — stored as `null`. */
const LIST_PRICE_LEVEL = 'list';

// ---------------------------------------------------------------------------
// Form contracts (zodResolver only — no Controller rules)
// ---------------------------------------------------------------------------

const addFormSchema = z
  .object({
    productId: z.string().min(1, 'pricing.currencyTab.modal.validation.productRequired'),
    priceLevelId: z.string().min(1, 'pricing.currencyTab.modal.validation.priceLevelRequired'),
    currency: z.string().min(1, 'pricing.currencyTab.modal.validation.currencyRequired'),
    price: z.number().nullable(),
  })
  .superRefine((values, ctx) => {
    if (values.price === null || values.price <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['price'],
        message: 'pricing.currencyTab.modal.validation.priceRequired',
      });
    }
  });

type AddFormData = z.infer<typeof addFormSchema>;

const defaultAddValues: AddFormData = {
  productId: '',
  priceLevelId: LIST_PRICE_LEVEL,
  currency: '',
  price: null,
};

const editFormSchema = z
  .object({ price: z.number().nullable() })
  .superRefine((values, ctx) => {
    if (values.price === null || values.price <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['price'],
        message: 'pricing.currencyTab.modal.validation.priceRequired',
      });
    }
  });

type EditFormData = z.infer<typeof editFormSchema>;

/** Pending save carried into the ReasonRequiredModal step (the only entry). */
type PendingAction =
  | { type: 'add'; row: CurrencyPriceRow }
  | { type: 'unlock'; row: CurrencyPriceRow }
  | { type: 'price'; row: CurrencyPriceRow };

const CurrencyTab = () => {
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { dataset, isLoading, error, refresh } = usePricingDataset();
  const { saveCurrencyPriceRow } = usePricingMutations();
  const { products } = useProductOptions();
  const { currencyOptions } = useCurrencies();
  // DA-7 — the ONE visibility gate for every write control on this tab.
  const writeSeat = canEditCostBasis();

  const [productFilter, setProductFilter] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  // Display truth (wave-1 precedent): product identity = name + SKU; the raw
  // `prod-*` id only ever appears as the honest fallback while options load.
  const productName = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p.displayName]));
    return (id: string) => byId.get(id) ?? id;
  }, [products]);

  const productSku = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p.sku]));
    return (id: string) => byId.get(id) ?? id;
  }, [products]);

  const levelById = useMemo(
    () => new Map((dataset?.priceLevels ?? []).map((level) => [level.id, level])),
    [dataset]
  );

  const levelLabel = (priceLevelId: string | null): string => {
    if (priceLevelId === null) return t('pricing:pricing.productSection.listPriceLabel');
    const level = levelById.get(priceLevelId);
    if (!level) return priceLevelId;
    const name = i18n.language.startsWith('zh') ? level.nameZh : level.nameEn;
    return `${level.code} · ${name}`;
  };

  // Platform currency list + every currency already present in the dataset —
  // the reference-currency picker stays usable when /api/currencies is thin
  // (OverridesTab precedent).
  const currencySelectOptions = useMemo(() => {
    const known = new Set(currencyOptions.map((option) => option.value as string));
    const datasetCurrencies = new Set<string>([
      ...(dataset?.priceGroupBands ?? []).map((band) => band.currency),
      ...(dataset?.costRecords ?? []).map((record) => record.currency),
      ...(dataset?.currencyPriceRows ?? []).map((row) => row.currency),
    ]);
    const extras = Array.from(datasetCurrencies)
      .filter((code) => !known.has(code))
      .sort()
      .map((code) => ({ label: code, value: code }));
    return [...currencyOptions, ...extras];
  }, [currencyOptions, dataset]);

  const allRows = useMemo(() => dataset?.currencyPriceRows ?? [], [dataset]);

  // Grouped-by-product listing: product, then currency, then level order
  // (list-price row first).
  const rows = useMemo(() => {
    const levelRank = (id: string | null): number =>
      id === null ? -1 : (levelById.get(id)?.sortOrder ?? Number.MAX_SAFE_INTEGER);
    const scoped = productFilter
      ? allRows.filter((row) => row.productId === productFilter)
      : allRows;
    return [...scoped].sort(
      (a, b) =>
        productName(a.productId).localeCompare(productName(b.productId)) ||
        a.currency.localeCompare(b.currency) ||
        levelRank(a.priceLevelId) - levelRank(b.priceLevelId)
    );
  }, [allRows, productFilter, productName, levelById]);

  /** Edit target derived from the STORE row — an unlock save re-renders it live. */
  const editingRow = useMemo(
    () => (editingRowId ? (allRows.find((row) => row.id === editingRowId) ?? null) : null),
    [allRows, editingRowId]
  );

  const {
    control: addControl,
    handleSubmit: handleAddSubmit,
    reset: resetAdd,
    trigger: triggerAdd,
    formState: { errors: addErrors },
  } = useForm<AddFormData>({
    resolver: zodResolver(addFormSchema),
    defaultValues: defaultAddValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm<EditFormData>({
    resolver: zodResolver(editFormSchema),
    defaultValues: { price: null },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const openAdd = (prefillProductId?: string) => {
    resetAdd({ ...defaultAddValues, productId: prefillProductId ?? '' });
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    resetAdd(defaultAddValues);
  };

  const openEdit = (row: CurrencyPriceRow) => {
    setEditingRowId(row.id);
    resetEdit({ price: row.price });
  };

  const closeEdit = () => {
    setEditingRowId(null);
    resetEdit({ price: null });
  };

  /** Add form OK → build the row, then demand the reason (never save without it). */
  const onAddValid = (values: AddFormData) => {
    setPendingAction({
      type: 'add',
      row: {
        id: `cpr-${Date.now()}`,
        productId: values.productId,
        priceLevelId: values.priceLevelId === LIST_PRICE_LEVEL ? null : values.priceLevelId,
        currency: values.currency,
        price: values.price ?? 0,
        fixedPrice: false,
      },
    });
  };

  /** Price edit OK (only reachable on an UNLOCKED row) → reason step. */
  const onEditValid = (values: EditFormData) => {
    if (!editingRow) return;
    setPendingAction({
      type: 'price',
      row: { ...editingRow, price: values.price ?? editingRow.price },
    });
  };

  /** The explicit unlock step — its own audited save at the SAME price. */
  const requestUnlock = () => {
    if (!editingRow) return;
    setPendingAction({ type: 'unlock', row: { ...editingRow, fixedPrice: false } });
  };

  const onReasonSubmit = (reason: string) => {
    if (!pendingAction) return;
    try {
      saveCurrencyPriceRow(pendingAction.row, reason);
      if (pendingAction.type === 'unlock') {
        // Unlock keeps the edit modal open — the price field is active now.
        message.success(t('pricing:pricing.currencyTab.messages.unlocked'));
      } else {
        message.success(t('pricing:pricing.currencyTab.messages.saved'));
        if (pendingAction.type === 'add') closeAdd();
        else closeEdit();
      }
      setPendingAction(null);
    } catch (guardError) {
      if (guardError instanceof PricingGuardError && guardError.code === 'FIXED_PRICE_LOCKED') {
        message.error(t('pricing:pricing.currencyTab.messages.lockedRefused'));
      } else {
        message.error(t('pricing:pricing.currencyTab.messages.reasonRequired'));
      }
    }
  };

  const lockedTag = (
    <Tooltip title={t('pricing:pricing.currencyTab.lockedTooltip')}>
      <Tag icon={<LockOutlined />} style={{ margin: 0 }}>
        {t('pricing:pricing.currencyTab.lockedTag')}
      </Tag>
    </Tooltip>
  );

  const columns: TableProps<CurrencyPriceRow>['columns'] = [
    {
      title: t('pricing:pricing.currencyTab.fields.product'),
      key: 'product',
      width: 240,
      fixed: 'left',
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{productName(row.productId)}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {productSku(row.productId)}
          </span>
        </div>
      ),
    },
    {
      title: t('pricing:pricing.currencyTab.fields.priceLevel'),
      key: 'priceLevel',
      width: 200,
      render: (_: unknown, row) => <span>{levelLabel(row.priceLevelId)}</span>,
    },
    {
      title: t('pricing:pricing.currencyTab.fields.currency'),
      key: 'currency',
      width: 110,
      render: (_: unknown, row) => <Tag style={{ margin: 0 }}>{row.currency}</Tag>,
    },
    {
      title: t('pricing:pricing.currencyTab.fields.price'),
      key: 'price',
      width: 200,
      align: 'right',
      render: (_: unknown, row) => (
        <span className="tw-inline-flex tw-items-center tw-gap-2">
          {row.fixedPrice && lockedTag}
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: token.fontWeightStrong }}>
            {formatMoney(row.currency, row.price)}
          </span>
        </span>
      ),
    },
    {
      title: t('pricing:pricing.currencyTab.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) =>
        // DA-7: write seat only — HIDDEN for other roles (hide-don't-disable).
        writeSeat ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: t('pricing:pricing.currencyTab.actions.edit'),
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    openEdit(row);
                  },
                },
              ],
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              aria-label={t('pricing:pricing.currencyTab.actions.actionsFor', { id: row.id })}
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        ) : null,
    },
  ];

  const fieldErrorText = (key?: string) =>
    key ? <Text style={formStyles.errorStyle}>{t(key)}</Text> : null;

  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  /** Read-only identity line in the edit modal (W6 — text, never an input). */
  const readOnlyField = (label: string, value: ReactNode) => (
    <div>
      <Text style={formStyles.labelStyle}>{label}</Text>
      <Text strong style={{ display: 'block' }}>
        {value}
      </Text>
    </div>
  );

  return (
    <>
      <Text
        type="secondary"
        style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: token.marginMD }}
      >
        {t('pricing:pricing.currencyTab.hint')}
      </Text>

      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Select
          value={productFilter ?? undefined}
          onChange={(value) => setProductFilter(value ?? null)}
          allowClear
          showSearch
          optionFilterProp="label"
          size="large"
          placeholder={t('pricing:pricing.currencyTab.productFilterPlaceholder')}
          options={products.map((product) => ({
            label: `${product.displayName} (${product.sku})`,
            value: product.id,
          }))}
          style={{ width: '450px', borderRadius: radiusTokens.card }}
          aria-label={t('pricing:pricing.currencyTab.productFilterAria')}
        />
        {writeSeat && (
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => openAdd(productFilter ?? undefined)}
            style={{
              marginLeft: 'auto',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
          >
            {t('pricing:pricing.currencyTab.addButton')}
          </Button>
        )}
      </div>

      <TableContainer
        errorState={{
          active: !!error && !dataset,
          title: t('pricing:pricing.currencyTab.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<CurrencyPriceRow>
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={isLoading}
          locale={{
            // Load-bearing empty copy: the product-scoped state EXPLAINS the
            // downstream `no_currency_price` refusal; the global state is the
            // honest "nothing enabled yet" — neither is the generic triad.
            emptyText: productFilter ? (
              <ListEmptyState
                title={t('pricing:pricing.currencyTab.states.noRowsForProduct')}
                actionLabel={
                  writeSeat
                    ? t('pricing:pricing.currencyTab.states.enableForProduct')
                    : undefined
                }
                onAction={writeSeat ? () => openAdd(productFilter) : undefined}
              />
            ) : (
              <ListEmptyState title={t('pricing:pricing.currencyTab.states.noRows')} />
            ),
          }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 900 }}
        />
      </TableContainer>

      {/* ADD — enable a currency for a product cell (write seat only) */}
      {writeSeat && (
        <Modal
          open={addOpen}
          title={t('pricing:pricing.currencyTab.modal.addTitle')}
          onCancel={closeAdd}
          width={640}
          footer={[
            <Button
              key="cancel"
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={closeAdd}
            >
              {t('common:actions.cancel')}
            </Button>,
            <Button
              key="add"
              type="primary"
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={() => void handleAddSubmit(onAddValid)()}
            >
              {t('common:actions.add')}
            </Button>,
          ]}
        >
          <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  {requiredMark}
                  {t('pricing:pricing.currencyTab.modal.productLabel')}
                </Text>
                <Controller
                  name="productId"
                  control={addControl}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onChange={(value) => {
                        field.onChange(value);
                        triggerAdd('productId');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      options={products.map((product) => ({
                        label: `${product.displayName} (${product.sku})`,
                        value: product.id,
                      }))}
                      status={addErrors.productId ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.currencyTab.modal.productLabel')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.productId?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  {requiredMark}
                  {t('pricing:pricing.currencyTab.modal.priceLevelLabel')}
                </Text>
                <Controller
                  name="priceLevelId"
                  control={addControl}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onChange={(value) => {
                        field.onChange(value);
                        triggerAdd('priceLevelId');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      options={[
                        {
                          label: t('pricing:pricing.productSection.listPriceLabel'),
                          value: LIST_PRICE_LEVEL,
                        },
                        ...(dataset?.priceLevels ?? [])
                          .filter((level) => level.isActive)
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map((level) => ({ label: levelLabel(level.id), value: level.id })),
                      ]}
                      status={addErrors.priceLevelId ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.currencyTab.modal.priceLevelLabel')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.priceLevelId?.message)}
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  {requiredMark}
                  {t('pricing:pricing.currencyTab.modal.currencyLabel')}
                </Text>
                <Controller
                  name="currency"
                  control={addControl}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onChange={(value) => {
                        field.onChange(value);
                        triggerAdd('currency');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      options={currencySelectOptions}
                      status={addErrors.currency ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.currencyTab.modal.currencyLabel')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.currency?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  {requiredMark}
                  {t('pricing:pricing.currencyTab.modal.priceLabel')}
                </Text>
                <Controller
                  name="price"
                  control={addControl}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? null)}
                      onBlur={field.onBlur}
                      size="large"
                      min={0}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={addErrors.price ? 'error' : undefined}
                      aria-label={t('pricing:pricing.currencyTab.modal.priceLabel')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.price?.message)}
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                  {t('pricing:pricing.currencyTab.modal.priceHint')}
                </Text>
              </Col>
            </Row>
          </div>
        </Modal>
      )}

      {/* EDIT — price only; identity is read-only text; unlock-first on lock */}
      {writeSeat && (
        <Modal
          open={!!editingRow}
          title={t('pricing:pricing.currencyTab.modal.editTitle')}
          onCancel={closeEdit}
          width={640}
          footer={
            editingRow?.fixedPrice
              ? [
                  <Button
                    key="cancel"
                    size="large"
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                    onClick={closeEdit}
                  >
                    {t('common:actions.cancel')}
                  </Button>,
                  <Button
                    key="unlock"
                    type="primary"
                    size="large"
                    icon={<UnlockOutlined />}
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                    onClick={requestUnlock}
                  >
                    {t('pricing:pricing.currencyTab.modal.unlockButton')}
                  </Button>,
                ]
              : [
                  <Button
                    key="cancel"
                    size="large"
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                    onClick={closeEdit}
                  >
                    {t('common:actions.cancel')}
                  </Button>,
                  <Button
                    key="save"
                    type="primary"
                    size="large"
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                    onClick={() => void handleEditSubmit(onEditValid)()}
                  >
                    {t('common:actions.save')}
                  </Button>,
                ]
          }
        >
          {editingRow && (
            <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '12px', padding: '20px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  {readOnlyField(
                    t('pricing:pricing.currencyTab.modal.productLabel'),
                    <>
                      {productName(editingRow.productId)}
                      <Text
                        type="secondary"
                        style={{ display: 'block', fontSize: token.fontSizeSM }}
                      >
                        {productSku(editingRow.productId)}
                      </Text>
                    </>
                  )}
                </Col>
                <Col span={6}>
                  {readOnlyField(
                    t('pricing:pricing.currencyTab.modal.priceLevelLabel'),
                    levelLabel(editingRow.priceLevelId)
                  )}
                </Col>
                <Col span={6}>
                  {readOnlyField(
                    t('pricing:pricing.currencyTab.modal.currencyLabel'),
                    editingRow.currency
                  )}
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col span={24}>
                  {editingRow.fixedPrice ? (
                    // W6 lock presentation: read-only text + LockOutlined +
                    // tag — the price INPUT does not exist while locked
                    // (never a disabled input).
                    <div>
                      <Text style={formStyles.labelStyle}>
                        {t('pricing:pricing.currencyTab.modal.priceLabel')}{' '}
                        <LockOutlined
                          style={{ fontSize: token.sizeXS, color: token.colorTextTertiary }}
                        />
                      </Text>
                      <span className="tw-inline-flex tw-items-center tw-gap-2">
                        <Text
                          strong
                          style={{ fontVariantNumeric: 'tabular-nums', display: 'block' }}
                        >
                          {formatMoney(editingRow.currency, editingRow.price)}
                        </Text>
                        {lockedTag}
                      </span>
                      <Text
                        type="secondary"
                        style={{ display: 'block', marginTop: '4px', fontSize: token.fontSizeSM }}
                      >
                        {t('pricing:pricing.currencyTab.modal.lockedPriceExplain')}
                      </Text>
                    </div>
                  ) : (
                    <div>
                      <Text style={formStyles.labelStyle}>
                        {requiredMark}
                        {t('pricing:pricing.currencyTab.modal.priceLabel')}
                      </Text>
                      <Controller
                        name="price"
                        control={editControl}
                        render={({ field }) => (
                          <InputNumber
                            value={field.value}
                            onChange={(value) => field.onChange(value ?? null)}
                            onBlur={field.onBlur}
                            size="large"
                            min={0}
                            style={{ ...formStyles.inputStyle, width: '100%' }}
                            status={editErrors.price ? 'error' : undefined}
                            aria-label={t('pricing:pricing.currencyTab.modal.priceLabel')}
                          />
                        )}
                      />
                      {fieldErrorText(editErrors.price?.message)}
                      <Text
                        type="secondary"
                        style={{ fontSize: token.fontSizeSM, display: 'block' }}
                      >
                        {t('pricing:pricing.currencyTab.modal.priceHint')}
                      </Text>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Modal>
      )}

      {/* The ONE reason gate — add, unlock and price edit all land here */}
      <ReasonRequiredModal
        open={pendingAction !== null}
        title={t('pricing:pricing.currencyTab.reasonModal.title')}
        description={t('pricing:pricing.currencyTab.reasonModal.description')}
        submitText={t('pricing:pricing.currencyTab.reasonModal.submit')}
        cancelText={t('common:actions.cancel')}
        reasonLabel={t('pricing:pricing.currencyTab.reasonModal.reasonLabel')}
        reasonPlaceholder={t('pricing:pricing.currencyTab.reasonModal.reasonPlaceholder')}
        requiredMessage={t('pricing:pricing.currencyTab.reasonModal.requiredMessage')}
        onSubmit={onReasonSubmit}
        onCancel={() => setPendingAction(null)}
      />
    </>
  );
};

export default CurrencyTab;
